import { jwtDecode } from "jwt-decode";
import {
  AnsweringClient,
  AppliedCouponResponse,
  CartResponse,
} from "../clients/answeringClient";

/**
 * Thrown only when the session is provably dead from local token state: no
 * access token to send, or — after a refresh attempt has not produced tokens —
 * both the sent access token and the stored refresh token are absent,
 * undecodable, or past exp. A refresh response can never prove death (the
 * backend collapses a dead token and an auth outage into one 401), and no
 * local reading may skip the refresh itself: a 403 beside a live-looking
 * token can still be an authentication failure a fresh token fixes.
 */
export class SessionExpiredError extends Error {
  constructor(cause?: unknown) {
    super("Sua sessão expirou. Entre novamente.");
    this.name = "SessionExpiredError";
    this.cause = cause;
  }
}

/**
 * Thrown when concurrent writes for one instrument carry different quantities —
 * against the additive add route, failing loudly beats dropping or duplicating
 * one of the two writes. Near-unreachable: callers hold in-flight guards,
 * though CheckoutReview's is released early by a cold-cache CART_UPDATED, so
 * after a degraded write this can surface there as the generic transient text.
 */
export class CartWriteInvariantError extends Error {
  constructor(instrumentId: number) {
    super(
      `Concurrent cart writes with different quantities for instrument ${instrumentId}`
    );
    this.name = "CartWriteInvariantError";
  }
}

// Additive, so a borderline token survives rather than logging the user out.
const TOKEN_DEATH_GRACE_MS = 5 * 60 * 1000;

function isTokenLocallyDead(token: string | null): boolean {
  if (!token) {
    return true;
  }
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (typeof exp !== "number") {
      return true;
    }
    return exp * 1000 + TOKEN_DEATH_GRACE_MS <= Date.now();
  } catch {
    return true;
  }
}

export interface ICartItem {
  id: number;
  instrumentId: number;
  title: string;
  quantity: number;
  unitPriceCents: number;
  subtotalCents: number;
  discountedUnitPriceCents: number;
  discountedSubtotalCents: number;
  totalDiscountCents: number;
  appliedDiscountPct: number | null;
  priceSnapshotCents: number | null;
  priceChanged: boolean;
}

export interface ICartSnapshot {
  id: number;
  status: string;
  items: ICartItem[];
  itemsCount: number;
  subtotalCents: number;
  progressiveSubtotalCents: number;
  progressiveDiscountCents: number;
  couponDiscountCents: number;
  totalDiscountCents: number;
  totalCents: number;
  appliedCoupon: AppliedCouponResponse | null;
  updatedAt: string;
}

enum CartSingletonEvents {
  CART_UPDATED = "cartUpdated",
}

export class CartSingleton {
  private static _sharedInstance: CartSingleton;

  public static shared = (() => {
    if (!CartSingleton._sharedInstance) {
      CartSingleton._sharedInstance = new CartSingleton();
    }
    return CartSingleton._sharedInstance;
  })();

  private readonly notification: EventTarget =
    typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel("CartSingletonNotificationChannel")
      : new EventTarget();
  private readonly answeringClient = new AnsweringClient();
  private readonly listeners = new Map<() => void, EventListener>();
  private cart: ICartSnapshot | null = null;
  private couponCode: string | null = null;
  private readonly pendingWrites = new Map<
    number,
    { promise: Promise<void>; quantity: number }
  >();

  public addCartUpdatedListener(callback: () => void) {
    const handler: EventListener = () => callback();
    this.listeners.set(callback, handler);
    this.notification.addEventListener(CartSingletonEvents.CART_UPDATED, handler);
  }

  public removeCartUpdatedListener(callback: () => void) {
    const handler = this.listeners.get(callback);
    if (!handler) {
      return;
    }
    this.notification.removeEventListener(CartSingletonEvents.CART_UPDATED, handler);
    this.listeners.delete(callback);
  }

  public getCartSnapshot(): ICartSnapshot | null {
    return this.cart;
  }

  public getAllItems(): ICartItem[] {
    return this.cart?.items ?? [];
  }

  public getCartInstrument(instrumentId: number): ICartItem | null {
    if (!this.cart) {
      return null;
    }
    return (
      this.cart.items.find((item) => item.instrumentId === instrumentId) ?? null
    );
  }

  public async ensureLoaded(): Promise<ICartSnapshot> {
    if (this.cart) {
      return this.cart;
    }
    return this.refreshCart();
  }

  public async refreshCart(coupon?: string | null): Promise<ICartSnapshot> {
    const normalizedCoupon =
      coupon !== undefined
        ? coupon && coupon.trim()
          ? coupon.trim()
          : null
        : this.couponCode;

    const cart = await this.withAuth(async (token) => {
      const response = await this.answeringClient.getAdminCart(
        token,
        normalizedCoupon ?? undefined
      );
      return this.mapCartResponse(response.data);
    });

    this.couponCode = cart.appliedCoupon?.code ?? null;
    this.setCart(cart);
    return cart;
  }

  /**
   * Ensures the instrument sits in the cart at `quantity`, serialized per
   * instrument: a concurrent caller asking for the same quantity joins the
   * in-flight write rather than issuing a second one against the additive add
   * route. Must stay non-`async` — the map entry is registered synchronously
   * at entry, and behind an `await` two same-tick callers would both find the
   * map empty and both write.
   *
   * Resolving means the cart holds the requested state, though
   * `reloadAfterWrite` may have invalidated the local snapshot; rejecting
   * means the operation did not complete, with the exception noted there.
   *
   * @throws CartWriteInvariantError when a concurrent write for the same
   * instrument carries a different quantity.
   */
  public setCartInstrument(
    instrumentId: number,
    quantity: number
  ): Promise<void> {
    const pending = this.pendingWrites.get(instrumentId);
    if (pending) {
      if (pending.quantity === quantity) {
        return pending.promise;
      }
      return Promise.reject(new CartWriteInvariantError(instrumentId));
    }

    const stored: Promise<void> = this.writeCartInstrument(
      instrumentId,
      quantity
    ).finally(() => {
      // Forward-insurance: the returned promise is the cleanup-attached one, so
      // no successor entry can exist while this cleanup is pending; the identity
      // check only matters if a future await ever lands ahead of the delete.
      if (this.pendingWrites.get(instrumentId)?.promise === stored) {
        this.pendingWrites.delete(instrumentId);
      }
    });
    this.pendingWrites.set(instrumentId, { promise: stored, quantity });
    return stored;
  }

  private async writeCartInstrument(
    instrumentId: number,
    quantity: number
  ): Promise<void> {
    await this.ensureLoaded();
    const existingItem =
      this.cart?.items.find((item) => item.instrumentId === instrumentId) ?? null;

    if (!existingItem && quantity <= 0) {
      this.notification.dispatchEvent(new Event(CartSingletonEvents.CART_UPDATED));
      return;
    }

    if (existingItem && existingItem.quantity === quantity) {
      this.notification.dispatchEvent(new Event(CartSingletonEvents.CART_UPDATED));
      return;
    }

    if (quantity <= 0) {
      if (existingItem) {
        await this.withAuth((token) =>
          this.answeringClient.deleteCartItem(
            token,
            existingItem.id,
            this.couponCode ?? undefined
          )
        );
      }
    } else if (existingItem) {
      await this.withAuth((token) =>
        this.answeringClient.updateCartItem(token, existingItem.id, {
          quantity,
        }, this.couponCode ?? undefined)
      );
    } else {
      await this.withAuth((token) =>
        this.answeringClient.addCartItem(token, {
          instrumentId,
          quantity,
        }, this.couponCode ?? undefined)
      );
    }

    await this.reloadAfterWrite();
  }

  public async clear(): Promise<void> {
    await this.withAuth((token) => this.answeringClient.clearCart(token));
    await this.reloadAfterWrite();
  }

  /**
   * Re-reads the cart after a write landed. A failed re-read invalidates the
   * cache to literal `null` and resolves anyway, without dispatching, since
   * listeners render a null snapshot as an empty cart. A `SessionExpiredError`
   * is rethrown after invalidating: session death outranks the landed write.
   */
  private async reloadAfterWrite(): Promise<void> {
    try {
      await this.refreshCart();
    } catch (error) {
      this.cart = null;
      console.warn("Cart re-read failed after a write", error);
      if (error instanceof SessionExpiredError) {
        throw error;
      }
    }
  }

  public getAppliedCoupon(): AppliedCouponResponse | null {
    return this.cart?.appliedCoupon ?? null;
  }

  public getAppliedCouponCode(): string | null {
    return this.couponCode;
  }

  public async applyCoupon(code: string): Promise<void> {
    const trimmed = code?.trim();
    if (!trimmed) {
      throw new Error("Informe um código de cupom.");
    }

    const snapshot = await this.ensureLoaded();
    const baseAmount =
      snapshot.progressiveSubtotalCents ?? snapshot.subtotalCents ?? 0;

    try {
      const validated = await this.withAuth((token) =>
        this.answeringClient
          .validateCouponCode(token, {
            code: trimmed,
            orderAmountCents: baseAmount,
            cartId: snapshot.id,
          })
          .then((response) => response.data)
      );

      this.couponCode = validated.code;
      await this.refreshCart(validated.code);
    } catch (error: any) {
      if (error instanceof SessionExpiredError) {
        throw error;
      }
      const message =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Não foi possível validar o cupom.";
      throw new Error(message);
    }
  }

  public async removeCoupon(): Promise<void> {
    if (!this.couponCode) {
      return;
    }
    this.couponCode = null;
    await this.refreshCart(null);
  }

  private setCart(cart: ICartSnapshot) {
    this.cart = {
      ...cart,
      items: [...cart.items],
      appliedCoupon: cart.appliedCoupon
        ? { ...cart.appliedCoupon }
        : null,
    };
    this.notification.dispatchEvent(new Event(CartSingletonEvents.CART_UPDATED));
  }

  private mapCartResponse(response: CartResponse): ICartSnapshot {
    return {
      id: response.id,
      status: response.status,
      items: (response.items ?? []).map((item) => ({
        id: item.id,
        instrumentId: item.instrumentId,
        title: item.title,
        quantity: item.quantity,
        unitPriceCents: item.unitPriceCents,
        subtotalCents: item.subtotalCents,
        discountedUnitPriceCents: item.discountedUnitPriceCents,
        discountedSubtotalCents: item.discountedSubtotalCents,
        totalDiscountCents: item.totalDiscountCents,
        appliedDiscountPct: item.appliedDiscountPct,
        priceSnapshotCents: item.priceSnapshotCents,
        priceChanged: item.priceChanged,
      })),
      itemsCount: response.itemsCount ?? 0,
      subtotalCents: response.subtotalCents ?? 0,
      progressiveSubtotalCents:
        response.progressiveSubtotalCents ?? response.subtotalCents ?? 0,
      progressiveDiscountCents: response.progressiveDiscountCents ?? 0,
      couponDiscountCents: response.couponDiscountCents ?? 0,
      totalDiscountCents: response.totalDiscountCents ?? 0,
      totalCents: response.totalCents ?? response.subtotalCents ?? 0,
      appliedCoupon: response.appliedCoupon
        ? { ...response.appliedCoupon }
        : null,
      updatedAt: response.updatedAt,
    };
  }

  private getAccessToken(): string | null {
    return localStorage.getItem("AccessToken");
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | null> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens = response.data as {
        accessToken: string;
        refreshToken: string;
      };
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error) {
      return null;
    }
  }

  private async withAuth<T>(
    operation: (token: string) => Promise<T>
  ): Promise<T> {
    const token = this.getAccessToken();
    if (!token) {
      throw new SessionExpiredError();
    }

    try {
      return await operation(token);
    } catch (error) {
      const axiosError = error as {
        response?: {
          status?: number;
        };
      };

      if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
        const tokens = await this.refreshTokens();
        if (!tokens) {
          // The access token is classified as sent, not re-read — a concurrent
          // caller may have rewritten storage.
          if (
            isTokenLocallyDead(token) &&
            isTokenLocallyDead(localStorage.getItem("RefreshToken"))
          ) {
            throw new SessionExpiredError(error);
          }
          // The original body on this path is usually the auth layer's English
          // diagnostic; a response-less error routes callers to their generic
          // pt-BR text.
          const transient = new Error(
            "Não foi possível concluir a operação. Tente novamente."
          );
          transient.cause = error;
          throw transient;
        }
        return operation(tokens.accessToken);
      }

      throw error;
    }
  }
}
